import React from "react";
import * as adminCart from "shared/api/adminCart";
import { orders as ordersApi } from "shared/api";

// The admin "New Order" cart. This is deliberately separate from the IBO's own
// cart (see useIboCart): items placed here become a brand-new order the admin
// creates on the IBO's behalf, with its own Order Options (Mark as WWG /
// Priority Shipping / Charge Card On File). Every line is fully editable and
// removable by the rep because nothing here belongs to the IBO yet.
//
// A line's identity for de-duplication is product + color + size, same as the
// IBO cart, so re-adding the same variation just bumps the quantity.
const signatureOf = (productId, color, size) =>
  [productId, color, size].filter(Boolean).join("::");

// Charge Card On File is intentionally OFF and cannot be turned on yet: there is
// no payment integration behind the admin, so the admin never bills the card on
// file. This also guarantees a Re-Ship (which seeds this cart) never re-charges
// the customer.
const DEFAULT_OPTIONS = {
  markAsWWG: false,
  priorityShipping: false,
  chargeCardOnFile: false,
};

export function useOrderCart(selectedIbo) {
  const [items, setItems] = React.useState([]);
  const [options, setOptions] = React.useState(DEFAULT_OPTIONS);

  // The New Order cart is a draft for ONE IBO. If the rep switches IBOs, reset
  // it so a draft built for one account can never be checked out against
  // another (mirrors how useIboCart re-seeds per IBO). Keyed on iboNumber so a
  // re-fetch of the same IBO object doesn't wipe an in-progress draft.
  const ownerIboRef = React.useRef(null);
  React.useEffect(() => {
    const iboKey = selectedIbo?.iboNumber ?? null;
    if (ownerIboRef.current === iboKey) return;
    ownerIboRef.current = iboKey;
    setItems([]);
    setOptions(DEFAULT_OPTIONS);
  }, [selectedIbo]);

  const addToCart = React.useCallback((product, quantity = 1, selection) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    const color = selection?.color;
    const size = selection?.size;
    const wasOverridden = selection?.wasOverridden || false;
    const productId = product.id ?? product.productId ?? product.sku;
    // A bundle's identity is its members' chosen variations — the same bundle
    // with a large shirt is a different line from one with a medium, so the
    // selections take part in the signature instead of the (absent) top-level
    // colour/size.
    const bundleSelections = selection?.bundleSelections || null;
    const bundleSig = bundleSelections
      ? bundleSelections
          .map(
            (s) => `${s.sku || s.productId}:${s.color || ""}:${s.size || ""}`
          )
          .join(",")
      : null;
    const sig = signatureOf(productId, color ?? bundleSig, size);
    const lineId = `order::${sig}`;
    setItems((prev) => {
      const existing = prev.find((item) => item.lineId === lineId);
      if (existing) {
        return prev.map((item) =>
          item.lineId === lineId
            ? { ...item, qty: (item.qty || 1) + qty, wasOverridden }
            : item
        );
      }
      return [
        ...prev,
        {
          ...product,
          lineId,
          productId,
          variationId: selection?.variationId ?? null,
          qty,
          selectedColor: color,
          selectedSize: size,
          bundleSelections,
          wasOverridden,
        },
      ];
    });
  }, []);

  const updateQty = React.useCallback((lineId, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    setItems((prev) =>
      prev.map((item) => (item.lineId === lineId ? { ...item, qty } : item))
    );
  }, []);

  const remove = React.useCallback((lineIds) => {
    const idSet = new Set(lineIds);
    setItems((prev) => prev.filter((item) => !idSet.has(item.lineId)));
  }, []);

  const clear = React.useCallback(() => {
    setItems([]);
    setOptions(DEFAULT_OPTIONS);
  }, []);

  // Seed the cart from a set of existing order line items (used by Re-Ship).
  // Appends to (rather than replaces) the current draft so an in-progress new
  // order isn't silently discarded; a re-shipped line whose product/color/size
  // matches an existing line merges and bumps the quantity. Charge Card On File
  // stays OFF so a re-ship never re-bills the customer.
  const seed = React.useCallback((seedItems, seedOptions) => {
    setItems((prev) => {
      const next = prev.map((item) => ({ ...item }));
      const bySig = new Map();
      next.forEach((item) => {
        bySig.set(
          signatureOf(item.productId, item.selectedColor, item.selectedSize),
          item
        );
      });
      (seedItems || []).forEach((item, index) => {
        const productId =
          item.productId ?? item.id ?? item.sku ?? `line_${index}`;
        const color = item.selectedColor ?? item.variation ?? null;
        const size = item.selectedSize ?? item.size ?? null;
        const sig = signatureOf(productId, color, size);
        const qty = Math.max(1, Math.floor(Number(item.qty)) || 1);
        const existing = bySig.get(sig);
        if (existing) {
          existing.qty = (existing.qty || 1) + qty;
          return;
        }
        const line = {
          ...item,
          lineId: `order::${sig}`,
          productId,
          qty,
          selectedColor: color,
          selectedSize: size,
          sourceOrderId: item.sourceOrderId ?? null,
        };
        bySig.set(sig, line);
        next.push(line);
      });
      return next;
    });
    // Keep the rep's current toggles when appending to a draft, but always
    // force Charge Card On File off.
    setOptions((prev) => ({
      ...prev,
      ...(seedOptions || {}),
      chargeCardOnFile: false,
    }));
  }, []);

  const setOption = React.useCallback((key, value) => {
    // Charge Card On File is a placeholder until a payment integration exists,
    // so ignore any attempt to switch it on.
    if (key === "chargeCardOnFile") return;
    setOptions((prev) => ({ ...prev, [key]: value }));
  }, []);

  const cartCount = React.useMemo(
    () => items.reduce((sum, item) => sum + (item.qty || 1), 0),
    [items]
  );

  // Create the order. Persist each line to the admin cart first (a real call by
  // default, a no-op when REACT_APP_USE_MOCK_API=true) so the admin cart and
  // order endpoints stay exercised, then POST the order and clear the draft.
  const placeOrder = React.useCallback(
    async ({ ibo, personId, shipToAddressId, tempAddress } = {}) => {
      const lines = items.map((item) => ({
        sku: item.sku ?? null,
        productId: item.productId ?? null,
        variationId: item.variationId ?? null,
        name: item.name ?? item.title ?? null,
        image: item.image ?? item.thumbnailUrl ?? null,
        quantity: item.qty || 1,
        selectedColor: item.selectedColor ?? null,
        selectedSize: item.selectedSize ?? null,
        unitPriceCents: Math.round(
          (Number(item.price ?? item.retailPrice ?? item.pm ?? 0) || 0) * 100
        ),
        sourceOrderId: item.sourceOrderId ?? null,
      }));
      await Promise.all(
        lines.map((line) =>
          adminCart
            .addItem({
              // The BFF cart-item whitelist is sku/variationId/quantity/
              // unitPriceCents (validation.js CART_ITEM_ALLOWED_FIELDS) —
              // anything else is dropped, and without sku the persisted row
              // can't be traced back to a product.
              sku: line.sku,
              variationId: line.variationId,
              quantity: line.quantity,
              unitPriceCents: line.unitPriceCents,
            })
            .catch((err) => {
              // Cart persistence is auxiliary to order placement — don't block
              // the order, but don't swallow the failure invisibly either.
              // eslint-disable-next-line no-console
              console.warn("admin cart addItem failed:", err);
            })
        )
      );
      const result = await ordersApi.create({
        iboId: ibo?.id ?? ibo?.iboNumber ?? null,
        userId: personId ?? null,
        // Which address on file this ships to. The BFF resolves it back to a
        // formatted address and snapshots it onto the order, so a later move
        // doesn't rewrite where this one went.
        shipToAddressId: shipToAddressId ?? null,
        // A one-off destination for this order, sent only when the rep entered
        // one. The BFF re-validates it and ignores it unless `shipToAddressId`
        // is the sentinel that says to use it, so a stray field can't redirect
        // an order that was meant for an address on file.
        tempAddress: tempAddress ?? null,
        options,
        items: lines,
      });
      await adminCart.clear().catch(() => {});
      clear();
      return result;
    },
    [items, options, clear]
  );

  return {
    items,
    options,
    cartCount,
    addToCart,
    updateQty,
    remove,
    clear,
    seed,
    setOption,
    placeOrder,
  };
}

export default useOrderCart;
